import React from "react";
import { Helmet } from "react-helmet-async";
import { BRAND } from "../lib/constants";

// Production origin — no `www` subdomain (per OTTO audit, Feb 2026).
// All canonicals resolve to `https://theooppolia.ca/{clean-path}` regardless
// of the host serving the page (preview, staging, or prod).
const SITE_URL = "https://theooppolia.ca";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_oppolia-showroom/artifacts/idbcl0vn_Accepted%20and%20Approved%20logo.png";
const TWITTER_HANDLE = "@Theooppolia";
const SITE_NAME_SHORT = "Theo-Oppolia"; // fallback used for social meta caps

// SEO title caps per current best-practice + OTTO audit:
//   Open Graph title: ≤ 60 chars (Facebook cut-off ~ 88, but 60 is safe)
//   Twitter title:    55–60 chars
// Full `<title>` stays uncapped so the tab / SERP line-1 still reads well.
const OG_TITLE_MAX = 60;
const TWITTER_TITLE_MAX = 60;

// Query-string keys that MUST be stripped from canonicals. Anything
// beginning with `utm_` is stripped automatically in addition to this list.
const CANONICAL_STRIP_PARAMS = new Set([
  "gclid", "fbclid", "msclkid", "yclid", "wbraid", "gbraid", "dclid",
  "ref", "referrer", "session", "sessionid", "sid",
  "mc_cid", "mc_eid", "_hsenc", "_hsmi", "hsCtaTracking",
]);

/**
 * Normalises a raw path to an SEO-safe absolute canonical URL.
 *   • strips utm_* / gclid / fbclid / ref / session-style tracking params
 *   • preserves legitimate query-string keys (e.g. `?cat=kitchen` on /products)
 *   • lower-cases the entire path (canonical URLs must be case-consistent)
 *   • trailing-slash rule: KEEP for the root `/`, DROP for everything else
 */
export function buildCanonical(rawPath) {
  if (!rawPath || typeof rawPath !== "string") return `${SITE_URL}/`;
  // Split path and query. Ignore hash — hashes are never indexed anyway.
  const [pathRaw, queryRaw = ""] = rawPath.split("?", 2);
  // Path — lowercase, ensure leading /, collapse duplicate slashes.
  let path = pathRaw.startsWith("/") ? pathRaw : `/${pathRaw}`;
  path = path.replace(/\/{2,}/g, "/").toLowerCase();
  // Trailing-slash normalisation
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  // Query — strip tracking, keep the rest sorted for stable output.
  const kept = [];
  if (queryRaw) {
    for (const kv of queryRaw.split("&")) {
      if (!kv) continue;
      const eq = kv.indexOf("=");
      const key = (eq === -1 ? kv : kv.slice(0, eq)).toLowerCase();
      if (!key) continue;
      if (key.startsWith("utm_")) continue;
      if (CANONICAL_STRIP_PARAMS.has(key)) continue;
      kept.push(kv);
    }
    kept.sort();
  }
  return kept.length ? `${SITE_URL}${path}?${kept.join("&")}` : `${SITE_URL}${path}`;
}

// Utility: hard-truncate at a word boundary so social previews don't
// end mid-word. Falls back to a hard cut if a single "word" exceeds max.
function capAtWord(str, max) {
  if (!str || str.length <= max) return str || "";
  const trimmed = str.slice(0, max);
  const lastSpace = trimmed.lastIndexOf(" ");
  return lastSpace > max * 0.6 ? trimmed.slice(0, lastSpace).trim() + "…"
                                : trimmed.trim() + "…";
}

/**
 * Seo wraps react-helmet-async with a clean API and a per-page JSON-LD slot.
 * Renders exactly ONE of each SEO-critical tag (title, description,
 * canonical, og:*, twitter:*) — the OTTO audit requires this per-page.
 * `prioritizeSeoTags` moves the block to the top of <head>.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | Oppolia Luxury Cabinet and Furniture`
                          : "Oppolia Luxury Cabinet and Furniture";
  const desc = description || "Theo-Oppolia · OPPOLIA Canada — luxury whole-home custom cabinetry, installation, and remodeling. Hamilton Experience Centre. Free consultation.";
  const url = buildCanonical(path);
  const img = image || DEFAULT_IMAGE;

  // Social titles must be short — cap them independently of `<title>`.
  const socialBase = title || SITE_NAME_SHORT;
  const ogTitle = capAtWord(socialBase, OG_TITLE_MAX);
  const twTitle = capAtWord(socialBase, TWITTER_TITLE_MAX);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      {/* Bilingual hreflang — the app serves EN and FR at the SAME URL
          via a client-side language switcher, so both variants point
          to the canonical. `x-default` covers other locales / bots. */}
      <link rel="alternate" hrefLang="en-CA" href={url} />
      <link rel="alternate" hrefLang="fr-CA" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_CA" />
      <meta property="og:locale:alternate" content="fr_CA" />
      <meta property="og:site_name" content={BRAND.name} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={twTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {jsonLd && (
        <script type="application/ld+json">
          {typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/** Build a BreadcrumbList JSON-LD from an array of {name, path}. */
export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: buildCanonical(it.path),
    })),
  };
}

export { SITE_URL };
